import { useQuizBrowserBack } from './quizHistoryGuard';
import { SETTINGS_ROUTES } from "../../settingProfile/settingsRoutes";
import { QUIZ_ROUTES } from "./quizRoutes";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import PreferredLanguageToggle from "../../components/PreferredLanguageToggle";
import { useSidebarData } from "../../hooks/useSidebarData";
import { useNotifications } from "../../hooks/useNotifications";
import { useQuizModes } from "../../hooks/useQuizModes";
import {
  buildQuizBackTarget,
  buildQuizFlowState,
  buildStartQuizState,
  getStoredQuizFlowState,
  persistQuizFlowState,
  navigateQuizBack,
  resolveQuizBackTarget,
} from "./quizFlowState";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../../questionBank/QuestionBankList.css";
import "./StartQuiz.css";
import "./QuizSelection.css";

const QuizSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const persistedFlowState = useMemo(() => getStoredQuizFlowState(), []);
  const flowState = useMemo(
    () => buildQuizFlowState(location.state, persistedFlowState),
    [location.state, persistedFlowState]
  );
  const resolvedBackTarget = resolveQuizBackTarget(flowState, QUIZ_ROUTES.selection, buildQuizBackTarget(QUIZ_ROUTES.start, buildStartQuizState(flowState)));
  useQuizBrowserBack(resolvedBackTarget);
  const { data: sidebarData } = useSidebarData();
  const { data: unreadNotificationsData } = useNotifications({ page: 1, unreadOnly: true });
  const [selectedMode, setSelectedMode] = useState(flowState.selectedMode?.id || "");
  const quizData = flowState?.quizData || {};
  const profile = sidebarData?.user_data || {};
  const profileName = profile?.name || profile?.user_name || "Student";
  const profileImage =
    profile?.profile_image || profile?.image || profile?.user_image || profile?.avatar || "";
  const unreadNotificationCount = Number(unreadNotificationsData?.pagination?.totalRecords || 0);
  const unreadNotificationLabel =
    unreadNotificationCount > 99 ? "99+" : String(unreadNotificationCount);
  const quizTitle = quizData?.quizName || quizData?.title || "Quiz";
  const quizId = useMemo(
    () => Number(quizData?.quizId || quizData?.quiz_id || quizData?.id || 0),
    [quizData?.id, quizData?.quizId, quizData?.quiz_id]
  );
  const {
    data: quizModes,
    isLoading: quizModesLoading,
    isFetching: quizModesFetching,
    isError: quizModesError,
  } = useQuizModes(
    {
      quiz_id: quizId,
    },
    {
      enabled: quizId > 0,
    }
  );

  useEffect(() => {
    persistQuizFlowState(flowState);
  }, [flowState]);

  useEffect(() => {
    if (!selectedMode && Array.isArray(quizModes) && quizModes.length > 0) {
      setSelectedMode(quizModes[0].id);
    }
  }, [quizModes, selectedMode]);

  const handleSelect = (id) => {
    setSelectedMode((currentValue) => (currentValue === id ? "" : id));
  };

  const handleSolveTest = (id) => {
    const chosenMode = Array.isArray(quizModes)
      ? quizModes.find((mode) => mode.id === id) || null
      : null;
    const selectedLang = String(
      quizData?.selected_lang ||
        flowState?.selected_lang ||
        chosenMode?.langShortName ||
        "EN"
    )
      .trim()
      .toUpperCase() || "EN";
    const instructionFlowState = buildQuizFlowState({
      ...flowState,
      selectedMode: chosenMode,
      selected_lang: selectedLang,
      quizBackTarget: buildQuizBackTarget(QUIZ_ROUTES.selection, { ...flowState, selectedMode: chosenMode, selected_lang: selectedLang }),
    });

    setSelectedMode(id);
    persistQuizFlowState(instructionFlowState);
    navigate(QUIZ_ROUTES.instruction, {
      state: instructionFlowState,
    });
  };

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .page-content {
          padding-right: 0;
        }
      `}</style>

      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page page-padding">
          <div className="start-quiz qbl-main">
            <div className="hideProfile-page">
              <div className="categories-page-header mb-0 start-quiz-shell">
                <div className="quiz-header-left">
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={() =>
                      navigateQuizBack(navigate, resolvedBackTarget)
                    }
                    aria-label="Go back"
                  >
                    <i className="bi bi-chevron-left" />
                  </button>
                </div>

                <div className="start-quiz-shell__actions">
                  <PreferredLanguageToggle profile={profile} />

                  <button
                    type="button"
                    className="start-quiz-shell__icon"
                    aria-label="Notifications"
                    onClick={() => navigate(SETTINGS_ROUTES.notifications)}
                  >
                    <i className="bi bi-bell" />
                    {unreadNotificationCount > 0 ? (
                      <span className="start-quiz-shell__badge">{unreadNotificationLabel}</span>
                    ) : null}
                  </button>

                  <div className="start-quiz-shell__profile" aria-hidden="true">
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt={profileName}
                        className="start-quiz-shell__profile-image"
                      />
                    ) : (
                      <span className="start-quiz-shell__profile-fallback">
                        {String(profileName).trim().charAt(0).toUpperCase() || "S"}
                      </span>
                    )}
                    <span className="start-quiz-shell__profile-name">Hi, {profileName}</span>
                    <i className="bi bi-chevron-down start-quiz-shell__profile-caret" />
                  </div>
                </div>
              </div>
            </div>

            <div className="quiz-selection-page">
              <div className="quiz-setup">
                <div className="quiz-setup-label">Quiz setup</div>
                <h1 className="quiz-setup-title">{quizTitle}</h1>

                <div className="quiz-mode-list">
                  {(quizModesLoading || quizModesFetching) &&
                    [...Array(2)].map((_, index) => (
                      <div
                        key={`loading-${index}`}
                        className="quiz-mode-card quiz-mode-card--shimmer"
                        aria-hidden="true"
                      >
                        <span className="dashboard-skeleton dashboard-skeleton--thumb quiz-mode-card__shimmer-icon" />

                        <div className="quiz-mode-content">
                          <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title quiz-mode-card__shimmer-title" />
                          <span className="dashboard-skeleton dashboard-skeleton--line quiz-mode-card__shimmer-line" />
                          <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short quiz-mode-card__shimmer-line quiz-mode-card__shimmer-line--short" />
                          <span className="dashboard-skeleton dashboard-skeleton--button quiz-mode-card__shimmer-button" />
                        </div>

                        <span className="dashboard-skeleton dashboard-skeleton--icon quiz-mode-card__shimmer-check" />
                      </div>
                    ))}

                  {!quizModesLoading && !quizModesFetching && quizModesError ? (
                    <div className="quiz-mode-card">
                      <div className="quiz-mode-icon evaluation-icon">
                        <i className="bi bi-exclamation-circle" />
                      </div>

                      <div className="quiz-mode-content">
                        <h3>Unable to load quiz modes</h3>
                        <p>Please refresh the page and try again.</p>
                      </div>
                    </div>
                  ) : null}

                  {!quizModesLoading &&
                  !quizModesFetching &&
                  !quizModesError &&
                  Array.isArray(quizModes) &&
                  quizModes.length === 0 ? (
                    <div className="quiz-mode-card">
                      <div className="quiz-mode-icon practice-icon">
                        <i className="bi bi-slash-circle" />
                      </div>

                      <div className="quiz-mode-content">
                        <h3>No quiz modes available</h3>
                        <p>This quiz does not have any selectable modes right now.</p>
                      </div>
                    </div>
                  ) : null}

                  {!quizModesLoading &&
                    !quizModesFetching &&
                    !quizModesError &&
                    quizModes.map((mode) => {
                      const isSelected = selectedMode === mode.id;

                      return (
                        <div
                          key={mode.id}
                          className={`quiz-mode-card ${isSelected ? "selected" : ""}`}
                          onClick={() => handleSelect(mode.id)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleSelect(mode.id);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className={`quiz-mode-icon ${mode.iconClass}`}>
                            <i className={`bi ${mode.icon}`} />
                          </div>

                          <div className="quiz-mode-content">
                            <h3>{mode.title}</h3>
                            <p>{mode.description}</p>

                            <button
                              type="button"
                              className="solve-test-btn"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleSolveTest(mode.id);
                              }}
                            >
                              <span>Solve Test</span>
                              <i className="bi bi-arrow-right" />
                            </button>
                          </div>

                          <label
                            className="quiz-checkbox"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              name="quizMode"
                              value={mode.id}
                              checked={isSelected}
                              onChange={() => handleSelect(mode.id)}
                            />

                            <span className="custom-checkbox">
                              {isSelected ? <i className="bi bi-check" /> : null}
                            </span>
                          </label>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default QuizSelection;
